import { FC, memo, useState, useEffect } from 'react';
import { useOfferContext } from '../hook/useOfferContext';
import { useUpdateOfferStatusAPI } from '../../../api/offer/useUpdateOfferStatusAPI';
import { useGetRequestDetailsAPI } from '../../../api/request/useGetRequestDetailsAPI';
import {
    Box,
    Button,
    MenuItem,
    TextField,
    Typography,
    Chip,
    Divider,
    Alert,
    Tabs,
    Tab,
    IconButton,
    CircularProgress,
    TablePagination,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import MessageIcon from '@mui/icons-material/Message';
import BarChartIcon from '@mui/icons-material/BarChart';
import { Modal } from '../../../components/Modal';
import OfferDataTable from './OfferDataTable';
import { MessageTimeline } from './MessageTimeline';
import { StatisticsTab } from './StatisticsTab';
import { Offer } from '../../../type/offer/Offer';
import { OfferPosition } from '../../../type/offer/OfferPosition';
import { OfferStatus } from '../../RequestsCreatePage/type/enums/OfferStatus';
import { RequestResponseDTO, OfferResponseDTO } from '../../../type/dto/RequestResponseDTO';
import { ImageGalleryModal } from './timeline/ImageGalleryModal';


type RequestEditModalProps = {
    requestId: string;
    open: boolean;
    onClose: () => void;
};

const RequestEditModal: FC<RequestEditModalProps> = ({ requestId, open, onClose }) => {
    const { offers, getRequestOffers } = useOfferContext();
    const { updateOfferStatus } = useUpdateOfferStatusAPI();
    const { getRequestDetails } = useGetRequestDetailsAPI();

    const [currentTab, setCurrentTab] = useState(0);
    const [updatedStatuses, setUpdatedStatuses] = useState<{ [key: string]: string }>({});
    const [requestDetails, setRequestDetails] = useState<RequestResponseDTO | null>(null);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [messagesPage, setMessagesPage] = useState(0);
    const [messagesPageSize, setMessagesPageSize] = useState(50);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);

    const offerStatusOptions = Object.values(OfferStatus);

    // Load messages when Messages tab is clicked
    useEffect(() => {
        if (currentTab === 1 && open && requestId && !requestDetails) {
            loadMessages();
        }
    }, [currentTab, open, requestId]);

    const loadMessages = async () => {
        setMessagesLoading(true);
        try {
            const details = await getRequestDetails(requestId, messagesPage, messagesPageSize);
            setRequestDetails(details);
        } catch (error) {
            console.error('Failed to load messages:', error);
        } finally {
            setMessagesLoading(false);
        }
    };

    const handleRefreshMessages = async () => {
        await loadMessages();
    };

    const handleMessagesPageChange = async (_event: unknown, newPage: number) => {
        setMessagesPage(newPage);
        setMessagesLoading(true);
        try {
            const details = await getRequestDetails(requestId, newPage, messagesPageSize);
            setRequestDetails(details);
        } catch (error) {
            console.error('Failed to load messages:', error);
        } finally {
            setMessagesLoading(false);
        }
    };

    const handleMessagesPageSizeChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const newSize = parseInt(event.target.value, 10);
        setMessagesPageSize(newSize);
        setMessagesPage(0);
        setMessagesLoading(true);
        try {
            const details = await getRequestDetails(requestId, 0, newSize);
            setRequestDetails(details);
        } catch (error) {
            console.error('Failed to load messages:', error);
        } finally {
            setMessagesLoading(false);
        }
    };

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setCurrentTab(newValue);
    };

    const handleStatusChange = (offerId: string, newStatus: string): void => {
        setUpdatedStatuses((prevStatuses) => ({
            ...prevStatuses,
            [offerId]: newStatus,
        }));
    };

    const handleSubmit = async (): Promise<void> => {
        try {
            await updateOfferStatus(updatedStatuses);
            await getRequestOffers(requestId);
            onClose();
        } catch (error) {
            console.error('Failed to update offer statuses', error);
        }
    };

    const handleClose = () => {
        setCurrentTab(0);
        setRequestDetails(null);
        setMessagesPage(0);
        onClose();
    };

    const renderOffers = () => {
        if (!offers || offers.length === 0) {
            return (
                <Alert severity="info" sx={{ my: 2 }}>
                    No offers for this request
                </Alert>
            );
        }

        return offers.map((offer: Offer, index: number) => {
            const tableHeaderNames = {
                description: 'Description',
                brand: 'Brand',
                price: 'Price',
                quantity: 'Quantity',
                deliveryTime: 'Delivery time',
                images: 'Image',
            };

            const tableCellsData = offer?.offerPositions?.map((position: OfferPosition) => ({
                description: position?.description,
                brand: position?.brand,
                price: position?.price,
                quantity: position?.quantity,
                deliveryTime: position?.deliveryTime,
                images: position?.images,
            }));

            const currentOfferStatus = offer?.status;
            const updatableOfferStatus = updatedStatuses[offer.id] || offer.status;

            return (
                <Box key={offer.id} sx={{ mb: 4 }}>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            Offer {index + 1}
                        </Typography>
                        <Chip
                            label={currentOfferStatus}
                            color={
                                currentOfferStatus === OfferStatus.ACCEPTED_BY_CLIENT ||
                                currentOfferStatus === OfferStatus.ACCEPTED_BY_OPERATOR ||
                                currentOfferStatus === OfferStatus.ACCEPTED_BY_OPERATOR_SUPERVISOR
                                    ? 'success'
                                    : currentOfferStatus === OfferStatus.CANCELLED_BY_CLIENT ||
                                      currentOfferStatus === OfferStatus.CANCELLED_BY_OPERATOR ||
                                      currentOfferStatus === OfferStatus.CANCELLED_BY_OPERATOR_SUPERVISOR
                                    ? 'error'
                                    : currentOfferStatus === OfferStatus.EXPIRED
                                    ? 'warning'
                                    : 'default'
                            }
                            size="small"
                        />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <OfferDataTable
                            tableName={`Offer ${index + 1} Details`}
                            tableHeaderNames={tableHeaderNames}
                            tableCellsData={tableCellsData}
                        />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <TextField
                            select
                            fullWidth
                            label="Change Status"
                            value={updatableOfferStatus}
                            onChange={(e) => handleStatusChange(offer.id, e.target.value)}
                            size="small"
                        >
                            {offerStatusOptions.map((status) => (
                                <MenuItem key={status} value={status}>
                                    {status}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>

                    {index < offers.length - 1 && <Divider sx={{ my: 3 }} />}
                </Box>
            );
        });
    };

    // Convert old offers to new format for Statistics tab
    const convertedOffers: OfferResponseDTO[] = offers?.map((offer: Offer) => ({
        id: offer.id,
        messageId: '', // Not available in current Offer type
        requestId: requestId,
        status: offer.status,
        createdAt: offer.createdAt || new Date().toISOString(),
        modifiedAt: new Date().toISOString(), // Not available in current Offer type
        offerPositions: offer.offerPositions?.map((pos: OfferPosition) => {
            const imageObjects = pos.images?.map(img => {
                if ('image' in img && 'imageThumb' in img) {
                    // It's already in the correct format (OfferPositionUploadedImage)
                    return {
                        id: img.id || '',
                        uniqueId: img.uniqueId || '',
                        image: img.image || '',
                        imageThumb: img.imageThumb || '',
                        groupId: img.groupId || '',
                    };
                }
                return {
                    id: '',
                    uniqueId: '',
                    image: '',
                    imageThumb: '',
                    groupId: '',
                };
            }).filter(img => img.image) || [];

            return {
                id: pos.id || '',
                offerId: offer.id,
                productName: pos.productName || '',
                brand: pos.brand || '',
                description: pos.description,
                price: pos.price || 0,
                quantity: pos.quantity || 1,
                deliveryTime: Array.isArray(pos.deliveryTime) ? pos.deliveryTime.join(', ') : pos.deliveryTime,
                notes: pos.notes,
                productCode: '', // Not available in current OfferPosition type
                supplier: pos.supplier,
                images: imageObjects,
            };
        }) || [],
    })) || [];

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title="Request Details"
            maxWidth="lg"
            fullWidth={true}
            actions={
                <>
                    <Button startIcon={<CloseIcon />} onClick={handleClose} sx={{ textTransform: 'none' }}>
                        Close
                    </Button>
                    {currentTab === 0 && (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<SaveIcon />}
                            onClick={handleSubmit}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            Save Changes
                        </Button>
                    )}
                </>
            }
        >
            <Box>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs
                        value={currentTab}
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.875rem',
                            },
                        }}
                    >
                        <Tab icon={<LocalOfferIcon />} iconPosition="start" label="Offers" />
                        <Tab icon={<MessageIcon />} iconPosition="start" label="Messages" />
                        <Tab icon={<BarChartIcon />} iconPosition="start" label="Statistics" />
                    </Tabs>
                </Box>


                {currentTab === 0 && <Box>{renderOffers()}</Box>}
                {currentTab === 1 && (
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Chat Messages
                            </Typography>
                            <IconButton
                                onClick={handleRefreshMessages}
                                disabled={messagesLoading}
                                size="small"
                                color="primary"
                            >
                                <RefreshIcon />
                            </IconButton>
                        </Box>

                        {messagesLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : requestDetails ? (
                            <>
                                <MessageTimeline
                                    messages={requestDetails.messages.content}
                                    client={requestDetails.client}
                                    operator={requestDetails.operator}
                                />
                                <TablePagination
                                    component="div"
                                    count={requestDetails.messages.totalElements}
                                    page={messagesPage}
                                    onPageChange={handleMessagesPageChange}
                                    rowsPerPage={messagesPageSize}
                                    onRowsPerPageChange={handleMessagesPageSizeChange}
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                                    sx={{
                                        borderTop: '1px solid',
                                        borderColor: 'divider',
                                        mt: 2,
                                    }}
                                />
                            </>
                        ) : (
                            <Alert severity="info">
                                Click refresh to load messages
                            </Alert>
                        )}
                    </Box>
                )}

                {currentTab === 2 && (
                    <Box>
                        <StatisticsTab offers={convertedOffers} />
                    </Box>
                )}
            </Box>

            <ImageGalleryModal
                imageUrl={selectedImage}
                onClose={() => setSelectedImage(null)}
            />
        </Modal>
    );
};

export default memo(RequestEditModal);